import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Form, Modal, Spinner } from 'react-bootstrap';
import adminSchemaApi from '../api/adminSchemaApi';

/**
 * Migrate Reports / Dashboards / Home Layouts.
 * direction=same: sandbox→sandbox (schema names may differ)
 * direction=prod: sandbox→prod (schema names must match)
 */
const AdminMetadataMigrationModal = ({
  show,
  onHide,
  companyId = null,
  selectedTargetUserId = null,
  defaultSourceSchema = '',
  defaultTargetSchema = '',
  defaultDirection = 'same',
  lockSchemas = false,
}) => {
  const [schemas, setSchemas] = useState([]);
  const [sourceSchema, setSourceSchema] = useState(defaultSourceSchema);
  const [targetSchema, setTargetSchema] = useState(defaultTargetSchema);
  const [direction, setDirection] = useState(defaultDirection);
  const [includeReports, setIncludeReports] = useState(true);
  const [includeDashboards, setIncludeDashboards] = useState(true);
  const [includeHomeLayouts, setIncludeHomeLayouts] = useState(true);
  const [plan, setPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!show) return;
    setError('');
    setResult(null);
    setPlan(null);
    setDirection(defaultDirection);
    setSourceSchema(defaultSourceSchema || '');
    setTargetSchema(defaultTargetSchema || '');
    (async () => {
      try {
        const list = await adminSchemaApi.fetchMigrationSchemas();
        setSchemas(Array.isArray(list) ? list : []);
      } catch {
        setSchemas([]);
      }
    })();
  }, [show, defaultSourceSchema, defaultTargetSchema, defaultDirection]);

  // When direction is prod, force matching schema names
  useEffect(() => {
    if (direction === 'prod' && sourceSchema) {
      setTargetSchema(sourceSchema);
    }
  }, [direction, sourceSchema]);

  const loadPlan = useCallback(async () => {
    if (!sourceSchema || !targetSchema) return;
    setPlanLoading(true);
    setError('');
    try {
      const data = await adminSchemaApi.fetchMetadataMigrationPlan(
        sourceSchema,
        direction === 'prod' ? sourceSchema : targetSchema,
        direction
      );
      setPlan(data);
    } catch (e) {
      setPlan(null);
      setError(e?.message || 'Failed to load plan');
    } finally {
      setPlanLoading(false);
    }
  }, [sourceSchema, targetSchema, direction]);

  useEffect(() => {
    if (!show || !sourceSchema || !targetSchema) return;
    loadPlan();
  }, [show, sourceSchema, targetSchema, direction, loadPlan]);

  const handleRun = async () => {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      if (!selectedTargetUserId) {
        throw new Error(
          'Set "Default admin for schema" on the company detail page first so report/dashboard owners remap correctly.'
        );
      }
      const tgt = direction === 'prod' ? sourceSchema : targetSchema;
      const data = await adminSchemaApi.runMetadataMigration({
        sourceSchema,
        targetSchema: tgt,
        direction,
        companyId,
        includeReports,
        includeDashboards,
        includeHomeLayouts,
        selectedTargetUserId,
      });
      setResult(data);
      await loadPlan();
    } catch (e) {
      setError(e?.message || 'Metadata migration failed');
    } finally {
      setRunning(false);
    }
  };

  const canRun =
    !!sourceSchema &&
    (direction === 'prod' || !!targetSchema) &&
    !!selectedTargetUserId &&
    (includeReports || includeDashboards || includeHomeLayouts) &&
    !running;

  return (
    <Modal show={show} onHide={onHide} centered size="lg" backdrop={running ? 'static' : true}>
      <Modal.Header closeButton={!running}>
        <Modal.Title className="admin-title">Migrate Reports / Dashboards / Home</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="admin-subtitle small mb-3">
          Copy saved reports, dashboards, and home layouts. Existing rows on the target are merged;
          owners are remapped to matching target users (or Default admin for schema).
        </p>

        {!selectedTargetUserId && (
          <Alert variant="warning" className="mb-3 small">
            Set <strong>Default admin for schema</strong> on the company detail page before running.
            Without it, report/dashboard owners stay on source user ids and show blank on the target
            (especially when users are not migrated).
          </Alert>
        )}

        {error && (
          <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-3">
            {error}
          </Alert>
        )}

        <Form.Group className="mb-3">
          <Form.Label className="small fw-semibold">Direction</Form.Label>
          <div>
            <Form.Check
              type="radio"
              id="meta-dir-same"
              name="metaDir"
              label="Sandbox → Sandbox (schema names may differ)"
              checked={direction === 'same'}
              onChange={() => setDirection('same')}
              disabled={running || lockSchemas}
            />
            <Form.Check
              type="radio"
              id="meta-dir-prod"
              name="metaDir"
              label="Sandbox → Production (schema name must match)"
              checked={direction === 'prod'}
              onChange={() => setDirection('prod')}
              disabled={running || lockSchemas}
            />
          </div>
        </Form.Group>

        <RowFields
          schemas={schemas}
          sourceSchema={sourceSchema}
          targetSchema={direction === 'prod' ? sourceSchema : targetSchema}
          setSourceSchema={setSourceSchema}
          setTargetSchema={setTargetSchema}
          direction={direction}
          disabled={running || lockSchemas}
        />

        <div className="mb-3">
          <Form.Label className="small fw-semibold">Include</Form.Label>
          <Form.Check
            type="checkbox"
            id="meta-reports"
            label="Reports"
            checked={includeReports}
            onChange={(e) => setIncludeReports(e.target.checked)}
            disabled={running}
          />
          <Form.Check
            type="checkbox"
            id="meta-dashboards"
            label="Dashboards"
            checked={includeDashboards}
            onChange={(e) => setIncludeDashboards(e.target.checked)}
            disabled={running}
          />
          <Form.Check
            type="checkbox"
            id="meta-home"
            label="Home Layouts"
            checked={includeHomeLayouts}
            onChange={(e) => setIncludeHomeLayouts(e.target.checked)}
            disabled={running}
          />
        </div>

        {planLoading && (
          <div className="text-muted small mb-3">
            <Spinner animation="border" size="sm" className="me-2" />
            Loading plan…
          </div>
        )}

        {plan && !planLoading && (
          <Alert variant={plan.targetSchemaExists ? 'warning' : 'info'} className="small mb-3">
            {(plan.warnings || []).map((w, i) => (
              <div key={i}>{w}</div>
            ))}
            <div className="mt-2">
              Source reports: {plan.groups?.reports?.source?.report ?? '—'}
              {' · '}
              Target reports: {plan.groups?.reports?.target?.report ?? '—'}
            </div>
            <div>
              Source dashboards: {plan.groups?.dashboards?.source?.dashboard ?? '—'}
              {' · '}
              Target dashboards: {plan.groups?.dashboards?.target?.dashboard ?? '—'}
            </div>
            <div>
              Source homes: {plan.groups?.homeLayouts?.source?.home ?? '—'}
              {' · '}
              Target homes: {plan.groups?.homeLayouts?.target?.home ?? '—'}
            </div>
          </Alert>
        )}

        {result && (
          <Alert variant={result.failed > 0 ? 'warning' : 'success'} className="small">
            <div>{result.message}</div>
            {(result.warnings || []).length > 0 && (
              <ul className="mb-0 mt-2 ps-3">
                {result.warnings.slice(0, 12).map((w, i) => (
                  <li key={i}>{w}</li>
                ))}
              </ul>
            )}
          </Alert>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button variant="outline-secondary" onClick={onHide} disabled={running}>
          Close
        </Button>
        <Button className="admin-primary-btn" onClick={handleRun} disabled={!canRun}>
          {running ? (
            <>
              <Spinner animation="border" size="sm" className="me-2" />
              Migrating…
            </>
          ) : (
            'Run metadata migration'
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

function RowFields({
  schemas,
  sourceSchema,
  targetSchema,
  setSourceSchema,
  setTargetSchema,
  direction,
  disabled,
}) {
  return (
    <div className="row g-2 mb-3">
      <div className="col-md-6">
        <Form.Label className="small">Source schema</Form.Label>
        <Form.Select
          value={sourceSchema}
          onChange={(e) => setSourceSchema(e.target.value)}
          disabled={disabled}
        >
          <option value="">Select…</option>
          {schemas.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </Form.Select>
      </div>
      <div className="col-md-6">
        <Form.Label className="small">
          Target schema {direction === 'prod' ? '(must match source)' : ''}
        </Form.Label>
        {direction === 'prod' ? (
          <Form.Control value={sourceSchema || ''} disabled readOnly />
        ) : (
          <Form.Select
            value={targetSchema}
            onChange={(e) => setTargetSchema(e.target.value)}
            disabled={disabled}
          >
            <option value="">Select…</option>
            {schemas.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </Form.Select>
        )}
      </div>
    </div>
  );
}

export default AdminMetadataMigrationModal;
